package com.arena_web.core.viewmodel

import com.arena_web.data.dto.CardAvatarAttemptDto
import com.arena_web.data.dto.ConfirmCardAvatarRequest
import com.arena_web.domain.repositories.CardRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class AvatarGenerationUiState(
    /** Latest attempt for this card stored on the backend (used as refine source if no new attempt yet). */
    val latestStoredAttemptId: String? = null,
    /** Attempt produced in the current session — what's shown in the "new" card. */
    val newAttempt: CardAvatarAttemptDto? = null,
    val isLoadingHistory: Boolean = false,
    val isGenerating: Boolean = false,
    val isConfirming: Boolean = false,
    val isConfirmed: Boolean = false,
    val error: String? = null,
)

class AvatarGenerationViewModel(
    private val cardRepository: CardRepository,
    private val cardId: String,
    // When set, the modal is editing this existing gallery card — a prompt
    // alone (no upload) regenerates from that card's current photo.
    private val editProfileCardId: String? = null,
) : BaseViewModel() {

    // Fresh per-modal-open session UUID. The backend uses it to scope all
    // attempt queries to this generation flow, so opening the modal for a
    // template that already has an existing ProfileCard never inherits
    // attempts from the other card.
    private val sessionId: String = randomSessionId()

    private val _uiState = MutableStateFlow(AvatarGenerationUiState())
    val uiState: StateFlow<AvatarGenerationUiState> = _uiState.asStateFlow()

    init {
        loadLatestAttempt()
    }

    private fun loadLatestAttempt() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoadingHistory = true) }
            cardRepository.getAvatarAttempts(cardId, sessionId)
                .onSuccess { attempts ->
                    val latest = attempts.maxByOrNull { it.createdAt }
                    // If the latest attempt was never confirmed, treat it as a pending generation
                    // so the user can keep iterating where they left off.
                    val pending = latest?.takeIf { !it.isConfirmed }
                    _uiState.update {
                        it.copy(
                            isLoadingHistory = false,
                            latestStoredAttemptId = latest?.id,
                            newAttempt = pending,
                        )
                    }
                }
                .onFailure {
                    _uiState.update { it.copy(isLoadingHistory = false) }
                }
        }
    }

    fun uploadPhoto(bytes: ByteArray, name: String, type: String, prompt: String? = null) {
        viewModelScope.launch {
            _uiState.update { it.copy(isGenerating = true, error = null) }

            cardRepository.generateAvatar(cardId, sessionId, bytes, name, type, prompt)
                .onSuccess { attempt ->
                    _uiState.update { it.copy(isGenerating = false, newAttempt = attempt) }
                    confirmAttempt(attempt)
                }
                .onFailure { e ->
                    _uiState.update {
                        it.copy(isGenerating = false, error = e.message ?: "Generation failed")
                    }
                }
        }
    }

    fun refine(prompt: String) {
        val state = _uiState.value
        val sourceAttemptId = state.newAttempt?.id ?: state.latestStoredAttemptId
        val cleanPrompt = prompt.trim()
        if (cleanPrompt.isEmpty()) return
        // Nothing to refine off and not editing an existing card → bail.
        if (sourceAttemptId == null && editProfileCardId == null) return
        viewModelScope.launch {
            _uiState.update { it.copy(isGenerating = true, error = null) }

            val result = if (sourceAttemptId != null) {
                cardRepository.refineAvatar(cardId, sessionId, sourceAttemptId, cleanPrompt)
            } else {
                // First edit pass — regenerate from the card's existing photo.
                cardRepository.generateFromCard(cardId, sessionId, editProfileCardId!!, cleanPrompt)
            }
            result
                .onSuccess { newAttempt ->
                    _uiState.update { it.copy(isGenerating = false, newAttempt = newAttempt) }
                    confirmAttempt(newAttempt)
                }
                .onFailure { e ->
                    _uiState.update {
                        it.copy(isGenerating = false, error = e.message ?: "Refinement failed")
                    }
                }
        }
    }

    // Auto-save: the moment a generation/refinement lands we confirm it as the
    // user's avatar (no manual Apply step). Success flips `isConfirmed`; the
    // modal stays open so the user can keep refining and only notifies the
    // caller to refresh on dismiss.
    private fun confirmAttempt(attempt: CardAvatarAttemptDto) {
        viewModelScope.launch {
            _uiState.update { it.copy(isConfirming = true, error = null) }

            // In edit mode, confirm replaces the edited card's photo in place
            // instead of appending a new gallery card.
            cardRepository.confirmAvatar(
                cardId,
                ConfirmCardAvatarRequest(attempt.id, sessionId, profileCardId = editProfileCardId),
            )
                .onSuccess {
                    _uiState.update { it.copy(isConfirming = false, isConfirmed = true) }
                }
                .onFailure { e ->
                    _uiState.update {
                        it.copy(isConfirming = false, error = e.message ?: "Confirm failed")
                    }
                }
        }
    }

    fun clearError() {
        _uiState.update { it.copy(error = null) }
    }
}

// Browser-native UUID v4 via Web Crypto. The backend parses the value as a
// UUID, so we hand it the standard 8-4-4-4-12 form. Falls back to a Random
// composition if `crypto.randomUUID` is unavailable (older browsers).
private fun randomSessionId(): String {
    val fromCrypto = js("(typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : null")
    if (fromCrypto != null) return fromCrypto as String

    fun hex(bits: Int): String {
        val max = 1L shl bits
        val v = (kotlin.random.Random.nextLong() and (max - 1)).toString(16)
        val width = bits / 4
        return v.padStart(width, '0')
    }
    // v4: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx (y ∈ {8,9,a,b})
    val y = "89ab"[kotlin.random.Random.nextInt(4)]
    return buildString {
        append(hex(32)); append('-')
        append(hex(16)); append('-')
        append('4'); append(hex(12)); append('-')
        append(y); append(hex(12)); append('-')
        append(hex(48))
    }
}
